// ============================================================================
// Anaglyphic, Netherlands @ 2024.
// See README in the root project for more information.
// ============================================================================

import { Ref } from "vue";

// ============================================================================

interface TabLike {
	nameClean: string;
	disabled?: boolean;
}

/**
 * Composable that cycles a set of dialog tabs with the Tab / Shift+Tab keys.
 *
 * Bind the returned handler to a keydown listener (e.g. `@keydown.tab`). Tab
 * advances to the next tab and Shift+Tab to the previous one, wrapping around
 * the ends and skipping any disabled tabs. `event.preventDefault()` is only
 * called when more than one tab is enabled, so native focus traversal is
 * preserved when cycling is a no-op.
 *
 * @param getTabs Returns the currently selectable tabs (re-evaluated per press).
 * @param activeTab The two-way bound model holding the active tab's `nameClean`.
 */
export function useTabCycling<T extends string>(
	getTabs: () => readonly TabLike[],
	activeTab: Ref<T | undefined>
) {
	function cycleAxisTab(event: KeyboardEvent) {
		const tabs = getTabs();
		const enabledCount = tabs.filter((tab) => !tab.disabled).length;
		if (enabledCount <= 1) {
			return;
		}

		event.preventDefault();
		const currentIndex = tabs.findIndex(
			(tab) => tab.nameClean === activeTab.value
		);
		const direction = event.shiftKey ? -1 : 1;
		let nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
		while (tabs[nextIndex].disabled) {
			nextIndex = (nextIndex + direction + tabs.length) % tabs.length;
		}
		activeTab.value = tabs[nextIndex].nameClean as T;
	}

	return { cycleAxisTab };
}
